import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, of, shareReplay, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  GetAssetByCustomIdGQL,
  GetAssetPreviewByCustomIdGQL,
  GetAssetPreviewDataGQL,
  GetCollectionByCustomIdGQL,
  GetEntityGQL,
  GetOrganizationByCustomIdGQL,
  IdProperty,
} from './graphql.services';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  private readonly collectionType = 'ViewingRoom';
  private readonly collections = new Map<string, Observable<any>>();
  mailchimpAPIUrl = environment.apiUrl;

  resourcesUrl = environment.assetsUrl;

  constructor(
    private http: HttpClient,
    private getAssetByCustomId: GetAssetByCustomIdGQL,
    private getEntity: GetEntityGQL,
    private getOrganizationByCustomId: GetOrganizationByCustomIdGQL,
    private getAssetPreviewData: GetAssetPreviewDataGQL,
    private getAssetPreviewByCustomId: GetAssetPreviewByCustomIdGQL,
    private getCollectionByCustomId: GetCollectionByCustomIdGQL
  ) {}

  getGeneralTexts() {
    return this.http.get(`${this.resourcesUrl}/general/texts_web.json`);
  }

  addSubscriber(data: Object): Observable<Object> {
    return this.http.post(this.mailchimpAPIUrl + '/mailchimp/signup', { data });
  }

  removeWhitespace(str) {
    return str.replace(/\s/g, '');
  }

  //GRAPHQL

  getArtworkByCustomId(artworkId) {
    return this._getArtworkByCustomId(artworkId);
  }

  fetchOrganization(customId) {
    return this._getOrganizationByCustomId(customId);
  }

  getAssetPreviews(customId, page, queryLimit) {
    return this.fetchCollectionByCustomId(customId).pipe(
      switchMap((collection) => {
        if (!collection?.id) {
          return of({ data: [], meta: null });
        }
        return this._getAssetPreviewById(
          collection.id,
          this.collectionType,
          page,
          queryLimit
        );
      })
    );
  }

  getAllAssetPreviews(customId: string, queryLimit: number) {
    return this.getAssetPreviews(customId, 1, queryLimit).pipe(
      switchMap((first) => {
        const meta = first.meta;
        if (!meta || meta.currentPage >= meta.lastPage) {
          return of([first]);
        }
        const rest = Array.from(
          { length: meta.lastPage - meta.currentPage },
          (_, i) =>
            this.getAssetPreviews(customId, meta.currentPage + 1 + i, queryLimit)
        );
        return forkJoin([of(first), ...rest]);
      }),
      map((pages) => ({
        data: pages.flatMap((page) => page.data || []),
        meta: null,
      }))
    );
  }

  getAssetPreview(customId) {
    return this._getAssetPreviewByCustomId(customId);
  }

  fetchCollectionByCustomId(customId: string) {
    return this._getCollectionByCustomId(customId);
  }

  fetchEntity(id) {
    return this._getEntity(id);
  }

  // PRIVATE ----------------------------------------------------

  private _getArtworkByCustomId(artworkId: string) {
    return this.getAssetByCustomId.fetch({ customId: artworkId }).pipe(
      map((res) => {
        const artworkData = res.data.assetByCustomId;
        return artworkData;
      })
    );
  }

  // v2 organization ids are numeric; the slug moved to customId, so the org has
  // to be looked up through listOrganizations' filtering arg.
  private _getOrganizationByCustomId(organizationCustomId) {
    return this.getOrganizationByCustomId
      .fetch({ customId: organizationCustomId }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((res) => {
          const organizationData =
            res.data?.listOrganizations?.data?.[0] ?? null;
          return organizationData;
        })
      );
  }

  private _getAssetPreviewById(id, collectionType, page, queryLimit) {
    return this.getAssetPreviewData
      .fetch(
        {
          id: id,
          property: IdProperty.Collection,
          collectionType,
          page: page,
          queryLimit: queryLimit,
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe(map((res) => res.data.assetContextsById));
  }

  private _getAssetPreviewByCustomId(customId) {
    return this.getAssetPreviewByCustomId
      .fetch({ customId: customId }, { fetchPolicy: 'no-cache' })
      .pipe(map((res) => res.data.assetByCustomId));
  }

  private _getCollectionByCustomId(customId: string) {
    const existing = this.collections.get(customId);
    if (existing) {
      return existing;
    }

    const collection = this.getCollectionByCustomId
      .fetch(
        { customId, collectionType: this.collectionType },
        { fetchPolicy: 'no-cache' }
      )
      .pipe(
        map((res) => res.data.collectionByCustomId ?? null),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.collections.set(customId, collection);
    return collection;
  }

  private _getEntity(id) {
    return this.getEntity.fetch({ id }).pipe(map((res) => res.data.entity));
  }
}
